import React, { createContext, useCallback, useContext, useState } from 'react'

interface CartDrawerContextType {
  isCartOpen: boolean
  openCart: () => void
  closeCart: () => void
  toggleCart: () => void
  showItemAddedConfirmation: (itemName: string) => void
  itemAddedNotification: { show: boolean; itemName: string } | null
}

const CartDrawerContext = createContext<CartDrawerContextType | undefined>(undefined)

export const CartDrawerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [itemAddedNotification, setItemAddedNotification] = useState<{ show: boolean; itemName: string } | null>(null)

  const openCart = useCallback(() => {
    setIsCartOpen(true)
  }, [])

  const closeCart = useCallback(() => {
    setIsCartOpen(false)
  }, [])

  const toggleCart = useCallback(() => {
    setIsCartOpen(prev => !prev)
  }, [])

  const showItemAddedConfirmation = useCallback((itemName: string) => {
    // Show notification
    setItemAddedNotification({ show: true, itemName })

    // Auto-hide after 3 seconds
    setTimeout(() => {
      setItemAddedNotification(null)
    }, 3000)

    // Open cart drawer
    setIsCartOpen(true)
  }, [])

  return (
    <CartDrawerContext.Provider
      value={{
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,
        showItemAddedConfirmation,
        itemAddedNotification
      }}
    >
      {children}
    </CartDrawerContext.Provider>
  )
}

export const useCartDrawer = () => {
  const context = useContext(CartDrawerContext)
  if (context === undefined) {
    throw new Error('useCartDrawer must be used within a CartDrawerProvider')
  }
  return context
}
